import type { BattleSummary } from "@loot-os/api-contract";
import { formatAmountWithUnit } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { buttonVariants } from "@loot-os/ui/components/button";
import { Card, CardContent } from "@loot-os/ui/components/card";
import { Link } from "@tanstack/react-router";

import { battleVisualStatus } from "../../lib/battleTimeline.js";
import { useCurrency } from "../../lib/currency.js";
import { BattleModeBadge } from "./BattleModeBadge.js";
import { BattleRoundsStrip, type RoundBox } from "./BattleRoundsStrip.js";
import { SeatName } from "./SeatName.js";

/** One battle in the lobby: what it costs, what it opens, who is in, and how to get in. */
export function BattleCard({
  battle,
  boxes,
  viewerEndUserId,
}: {
  battle: BattleSummary;
  boxes: ReadonlyMap<string, RoundBox>;
  viewerEndUserId: string | null | undefined;
}) {
  const currency = useCurrency();
  const status = battleVisualStatus(battle);
  const filled = battle.seats.filter((s) => s.kind !== "empty").length;
  const seated = battle.seats.some((s) => s.endUser?.id === viewerEndUserId);
  const cta =
    status === "joinable"
      ? seated
        ? "Open lobby"
        : "Join"
      : status === "in-progress"
        ? "Watch"
        : "Results";

  return (
    <Card className={status === "finished" ? "opacity-60" : undefined}>
      <CardContent className="grid gap-3 p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <BattleModeBadge mode={battle.mode} />
            {battle.isPublic ? null : (
              <Badge variant="outline" title="Only people with the link can join.">
                Private
              </Badge>
            )}
            {battle.fastSpin ? (
              <span className="text-xs text-muted-foreground">Fast spin</span>
            ) : null}
            {status === "in-progress" ? (
              <span className="text-xs text-primary">
                Round {Math.max(0, battle.currentRevealedRound) + 1} of {battle.roundCount}
              </span>
            ) : null}
          </div>
          <span className="font-mono text-sm font-semibold">
            {formatAmountWithUnit(battle.priceCents, currency)}
          </span>
        </div>
        <BattleRoundsStrip rounds={battle.rounds} boxes={boxes} />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-2">
            {battle.seats.map((seat) => (
              <SeatName key={seat.seatIndex} seat={seat} viewerEndUserId={viewerEndUserId} />
            ))}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">
              {filled}/{battle.playerCount} seats · {battle.roundCount}{" "}
              {battle.roundCount === 1 ? "round" : "rounds"}
            </span>
            <Link
              to="/battles/$battleId"
              params={{ battleId: battle.id }}
              className={buttonVariants({
                size: "sm",
                variant: status === "joinable" ? "default" : "secondary",
              })}
            >
              {cta}
            </Link>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
